"use client";

import { c as _c } from "react/compiler-runtime";
import { useLocale } from "next-intl";
import { useMemo, useState } from "react";
import { SafeImage } from "@/components/storefront/SafeImage";
import { getSwatchClass, isColorOption, isHexColor } from "@/lib/storefront/products/colorSwatch";
import type { OptionDefinition, VariantForCard } from "@/lib/types/variants";
interface VariantSelectorProps {
  optionNames: OptionDefinition[];
  variants: VariantForCard[];
  onVariantChange: (variant: VariantForCard | null) => void;
  /**
   * Called when a color value is picked, with that color's representative
   * image (the first active variant of the color that has an assigned image).
   * Lets the gallery preview the color before a full variant is selected.
   */
  onColorPreview?: (imageUrl: string | null) => void;
  /** The store's own colour codes (option_values.color_hex) by value name. */
  colorHexMap?: Record<string, string>;
}
export default function VariantSelector(t0) {
  const $ = _c(35);
  const {
    optionNames,
    variants,
    onVariantChange,
    onColorPreview,
    colorHexMap
  } = t0;
  const locale = useLocale();
  const isRTL = locale === "ar";
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [selected, setSelected] = useState(t1);
  let t2;
  if ($[1] !== optionNames) {
    t2 = optionNames.filter(_temp);
    $[1] = optionNames;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const options = t2;
  let t3;
  if ($[3] !== options || $[4] !== variants) {
    bb0: {
      const map = {};
      const colorKeys = options.filter(_temp2).map(_temp3);
      if (colorKeys.length === 0) {
        t3 = map;
        break bb0;
      }
      for (const v of variants) {
        if (!v.image_url || !v.is_active) {
          continue;
        }
        for (const key of colorKeys) {
          const val = v.options[key];
          if (val) {
            const mapKey = `${key}::${val}`;
            if (!map[mapKey]) {
              map[mapKey] = v.image_url;
            }
          }
        }
      }
      t3 = map;
    }
    $[3] = options;
    $[4] = variants;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const imageByValue = t3;
  let map_0;
  if ($[6] !== options || $[7] !== variants) {
    map_0 = {};
    for (const opt_2 of options) {
      const key_0 = opt_2.name_ar;
      const values = new Set();
      for (const v_0 of variants) {
        if (v_0.options[key_0]) {
          values.add(v_0.options[key_0]);
        }
      }
      map_0[key_0] = [...values];
    }
    $[6] = options;
    $[7] = variants;
    $[8] = map_0;
  } else {
    map_0 = $[8];
  }
  const optionValues = map_0;
  let t4;
  if ($[9] !== imageByValue || $[10] !== onColorPreview || $[11] !== onVariantChange || $[12] !== options || $[13] !== selected || $[14] !== variants) {
    t4 = function handleSelect(optionKey, value) {
      const next = {
        ...selected,
        [optionKey]: value
      };
      setSelected(next);
      const preview = imageByValue[`${optionKey}::${value}`];
      if (preview) {
        onColorPreview?.(preview);
      }
      const match = variants.find(v_1 => options.every(opt_3 => {
        const key_1 = opt_3.name_ar;
        return !next[key_1] || v_1.options[key_1] === next[key_1];
      }));
      const allSelected = options.every(opt_4 => next[opt_4.name_ar]);
      onVariantChange(allSelected ? match ?? null : null);
    };
    $[9] = imageByValue;
    $[10] = onColorPreview;
    $[11] = onVariantChange;
    $[12] = options;
    $[13] = selected;
    $[14] = variants;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const handleSelect = t4;
  let t5;
  if ($[16] !== colorHexMap || $[17] !== handleSelect || $[18] !== imageByValue || $[19] !== isRTL || $[20] !== optionValues || $[21] !== options || $[22] !== selected || $[23] !== variants) {
    let t6;
    if ($[25] !== colorHexMap || $[26] !== handleSelect || $[27] !== imageByValue || $[28] !== isRTL || $[29] !== optionValues || $[30] !== selected || $[31] !== variants) {
      t6 = opt_5 => {
        const key_2 = opt_5.name_ar;
        const label = isRTL ? opt_5.name_ar : opt_5.name_en;
        const values_0 = optionValues[key_2] ?? [];
        const isColor = isColorOption(opt_5.name_ar, opt_5.name_en);
        const selectedValue = selected[key_2];
        return <div key={key_2} className="flex flex-col gap-2.5"><p className="text-sm font-medium text-foreground">{label}{selectedValue && <span className="ms-2 text-muted-foreground font-normal">: {selectedValue}</span>}</p><div className="flex flex-wrap gap-2">{values_0.map(val_0 => {
              const isSelected = selectedValue === val_0;
              const available = variants.some(v_2 => v_2.options[key_2] === val_0 && v_2.is_active && (v_2.stock === null || v_2.stock > 0));
              if (isColor) {
                const colorImage = imageByValue[`${key_2}::${val_0}`] ?? null;
                if (colorImage) {
                  return <button key={val_0} type="button" onClick={() => handleSelect(key_2, val_0)} disabled={!available} title={val_0} aria-label={val_0} aria-pressed={isSelected} className={["relative h-12 w-12 overflow-hidden rounded-lg transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2", isSelected ? "ring-2 ring-primary ring-offset-2 ring-offset-background" : "ring-1 ring-border hover:ring-foreground/40", !available && "opacity-40 cursor-not-allowed"].filter(Boolean).join(" ")}><SafeImage src={colorImage} alt={val_0} fill={true} className="object-cover" sizes="48px" loading="lazy" />{!available && <span className="absolute inset-0 flex items-center justify-center bg-background/40 text-foreground/70"><span className="block h-px w-[140%] rotate-45 bg-current" /></span>}</button>;
                }
                const storeHex = colorHexMap?.[val_0];
                const hex = storeHex && isHexColor(storeHex) ? storeHex : null;
                const swatchClass = hex ? null : getSwatchClass(val_0);
                if (!hex && !swatchClass) {
                  return <button key={val_0} type="button" onClick={() => handleSelect(key_2, val_0)} disabled={!available} aria-pressed={isSelected} className={["min-w-12 px-4 py-2 rounded-lg text-sm font-medium border transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1", isSelected ? "bg-primary text-primary-foreground border-primary" : available ? "bg-background text-foreground border-border hover:border-primary" : "bg-subtle text-muted-foreground border-border opacity-50 cursor-not-allowed line-through"].join(" ")}>{val_0}</button>;
                }
                const ringClass = isSelected ? "ring-2 ring-primary ring-offset-2 ring-offset-background" : "ring-1 ring-border hover:ring-foreground/40";
                return <button key={val_0} type="button" onClick={() => handleSelect(key_2, val_0)} disabled={!available} title={val_0} aria-label={val_0} aria-pressed={isSelected} className={["relative h-7 w-7 rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2", ringClass, swatchClass, !available && "opacity-40 cursor-not-allowed"].filter(Boolean).join(" ")}>{hex && <svg viewBox="0 0 2 2" aria-hidden="true" className="absolute inset-0 h-full w-full"><circle cx="1" cy="1" r="1" fill={hex} /></svg>}{!available && <span className="absolute inset-0 flex items-center justify-center text-foreground/70"><span className="block h-px w-[140%] rotate-45 bg-current" /></span>}</button>;
              }
              return <button key={val_0} type="button" onClick={() => handleSelect(key_2, val_0)} disabled={!available} aria-pressed={isSelected} className={["min-w-12 px-4 py-2 rounded-lg text-sm font-medium border transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1", isSelected ? "bg-primary text-primary-foreground border-primary" : available ? "bg-background text-foreground border-border hover:border-primary" : "bg-subtle text-muted-foreground border-border opacity-50 cursor-not-allowed line-through"].join(" ")}>{val_0}</button>;
            })}</div></div>;
      };
      $[25] = colorHexMap;
      $[26] = handleSelect;
      $[27] = imageByValue;
      $[28] = isRTL;
      $[29] = optionValues;
      $[30] = selected;
      $[31] = variants;
      $[32] = t6;
    } else {
      t6 = $[32];
    }
    t5 = options.map(t6);
    $[16] = colorHexMap;
    $[17] = handleSelect;
    $[18] = imageByValue;
    $[19] = isRTL;
    $[20] = optionValues;
    $[21] = options;
    $[22] = selected;
    $[23] = variants;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  let t6;
  if ($[33] !== t5) {
    t6 = <div className="space-y-5">{t5}</div>;
    $[33] = t5;
    $[34] = t6;
  } else {
    t6 = $[34];
  }
  return t6;
}
function _temp3(opt_1) {
  return opt_1.name_ar;
}
function _temp2(opt_0) {
  return isColorOption(opt_0.name_ar, opt_0.name_en);
}
function _temp(opt) {
  return opt.name_ar?.trim();
}